/**
 * Konvention: Einsatz-IDs sind überall `number` (wie in der Datenbank/API).
 * Strings (URL, <select>-Werte) werden nur an der Kante mit `parseEinsatzId` bzw. `String(id)` umgewandelt.
 */
export interface EinsatzOption {
  id: number
  titel: string
  typ?: string
  /** Status in Großbuchstaben, z. B. LAUFEND, GEPLANT */
  status?: string
  /** true, wenn der Status zu den laufenden Stati gehört (LAUFEND, ALARMIERUNG, ABFAHRT_WACHE, ANKUNFT_EINSATZORT, EINSATZENDE) */
  laufend: boolean
  beginn?: string | null
  lat?: number | null
  lon?: number | null
  adresse?: string | null
  /** Originalobjekt der API */
  raw: any
}

export const LAUFEND_STATI = ['LAUFEND', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE']

/** "12" -> 12; alles andere (leer, 0, negativ, Text, Dezimal) -> null. */
export function parseEinsatzId(v: unknown): number | null {
  if (typeof v === 'number') return Number.isInteger(v) && v > 0 ? v : null
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
    const n = Number(v.trim())
    return n > 0 ? n : null
  }
  return null
}

export function normalizeEinsatz(raw: any): EinsatzOption | null {
  const id = parseEinsatzId(raw?.id)
  if (id === null) return null
  const status = String(raw.status ?? '').toUpperCase() || undefined
  return {
    id,
    titel: String(raw.titel ?? raw.title ?? `Einsatz ${id}`),
    typ: raw.typ ?? raw.type ?? undefined,
    status,
    laufend: !!status && LAUFEND_STATI.includes(status),
    beginn: raw.beginn ?? raw.beginn_geplant ?? raw.plannedStart ?? null,
    lat: raw.lat ?? raw.latitude ?? null,
    lon: raw.lon ?? raw.longitude ?? null,
    adresse: raw.adresse ?? raw.einsatzort ?? null,
    raw,
  }
}

/** Akzeptiert Array, { einsaetze: [] }, { items: [] }, { einsatz: {...}|null } oder ein einzelnes Einsatz-Objekt. */
export function normalizeEinsatzListe(data: any): EinsatzOption[] {
  let arr: any[]
  if (Array.isArray(data)) arr = data
  else if (Array.isArray(data?.einsaetze)) arr = data.einsaetze
  else if (Array.isArray(data?.items)) arr = data.items
  else if (data && typeof data === 'object' && 'einsatz' in data) arr = data.einsatz ? [data.einsatz] : []
  else if (data && typeof data === 'object' && data.id != null) arr = [data]
  else arr = []
  const seen = new Set<number>()
  const out: EinsatzOption[] = []
  for (const r of arr) {
    const e = normalizeEinsatz(r)
    if (e && !seen.has(e.id)) { seen.add(e.id); out.push(e) }
  }
  return out
}

const RANG: Record<string, number> = { LAUFEND: 0, ALARMIERUNG: 1, ABFAHRT_WACHE: 2, ANKUNFT_EINSATZORT: 3, EINSATZENDE: 3, GEPLANT: 4 }

/**
 * Standard-Vorbelegung (aus der Taktischen Karte): laufende Stati vor GEPLANT, dann frühester Beginn, dann kleinste ID.
 * Andere Stati (z. B. EINSATZBEREIT) werden nicht vorgeschlagen.
 */
export function suggestNachRang(liste: EinsatzOption[]): number | null {
  const rel = liste.filter(x => RANG[x.status ?? ''] !== undefined)
  if (!rel.length) return null
  const zeit = (x: EinsatzOption) => (x.beginn ? new Date(x.beginn).getTime() || Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER)
  rel.sort((a, b) => RANG[a.status!] - RANG[b.status!] || zeit(a) - zeit(b) || a.id - b.id)
  return rel[0].id
}

export type StatusTon = 'success' | 'info' | 'warning' | 'neutral'

/** Anzeige für den Status-Chip. */
export function einsatzStatusInfo(status?: string): { label: string; ton: StatusTon } {
  switch ((status ?? '').toUpperCase()) {
    case 'LAUFEND': return { label: 'Laufend', ton: 'success' }
    case 'ALARMIERUNG': return { label: 'Alarmierung', ton: 'success' }
    case 'ABFAHRT_WACHE': return { label: 'Abfahrt', ton: 'success' }
    case 'ANKUNFT_EINSATZORT': return { label: 'Vor Ort', ton: 'success' }
    case 'EINSATZENDE': return { label: 'Einsatzende', ton: 'success' }
    case 'GEPLANT': return { label: 'Geplant', ton: 'info' }
    case 'EINSATZBEREIT': return { label: 'Bereit', ton: 'info' }
    case 'AKTIV': return { label: 'Aktiv', ton: 'success' }
    case '': return { label: '', ton: 'neutral' }
    default: return { label: status!.charAt(0) + status!.slice(1).toLowerCase().replace(/_/g, ' '), ton: 'neutral' }
  }
}
